import React, { useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Play, RotateCcw, Settings2, Sparkles, Square, Trash2 } from 'lucide-react';
import { skipToken } from '@reduxjs/toolkit/dist/query';

import { Button, ChatMarkdown, TextArea } from 'components';
import { cancelChatStream, useAppSelector, useChatStreamQuery } from 'store';
import { useFilters } from 'context';

type BenchmarkItem = {
	id: string;
	text: string;
	result?: string;
	status: 'pending' | 'processing' | 'completed' | 'error';
	error?: string;
	retryCount: number; // Track how many times this prompt has been retried
	// Metrics tracking
	startTime?: number;
	endTime?: number;
	responseLength?: number;
};

// Track overall benchmark metrics
type BenchmarkMetrics = {
	startTime: number | null;
	endTime: number | null;
	completedCount: number;
	errorCount: number;
	processingTimes: number[];
	responseLengths: number[];
};

export function BenchmarkingTool() {
	const { selectedSystem, selectedFacility } = useAppSelector((state) => state.userState);
	const { endDate, earliest_date, startDate, surgeons, serviceLines, rooms, encounterTypes, blockNames } = useFilters();

	// Input state
	const [rawInput, setRawInput] = useState('');
	const [prompts, setPrompts] = useState<BenchmarkItem[]>([]);

	// Processing state
	const [currentIndex, setCurrentIndex] = useState(-1);
	const [isProcessing, setIsProcessing] = useState(false);

	// Track expanded rows for response details
	const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

	// Toggle row expansion
	const toggleRowExpansion = (id: string) => {
		setExpandedRows((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(id)) {
				newSet.delete(id);
			} else {
				newSet.add(id);
			}
			return newSet;
		});
	};

	// Request ID for stream
	const requestIdRef = useRef(0);

	// Metrics tracking state - must be declared before effects that use it
	const [metrics, setMetrics] = useState<BenchmarkMetrics>({
		startTime: null,
		endTime: null,
		completedCount: 0,
		errorCount: 0,
		processingTimes: [],
		responseLengths: [],
	});

	// Parse input into benchmark items (one per line)
	const parseBenchmarks = () => {
		const lines = rawInput.split('\n').filter((line) => line.trim() !== '');
		const newPrompts: BenchmarkItem[] = lines.map((line, idx) => ({
			id: `benchmark-${Date.now()}-${idx}`,
			text: line.trim(),
			status: 'pending',
			retryCount: 0,
		}));
		setPrompts(newPrompts);
	};

	// Add parsed benchmarks
	const handleAddBenchmarks = () => {
		if (!rawInput.trim()) return;
		// Reset metrics when adding new benchmarks
		setMetrics({
			startTime: null,
			endTime: null,
			completedCount: 0,
			errorCount: 0,
			processingTimes: [],
			responseLengths: [],
		});
		parseBenchmarks();
		setRawInput('');
	};

	// Clear all prompts
	const handleClearAll = () => {
		setPrompts([]);
		setCurrentIndex(-1);
		setIsProcessing(false);
		// Reset metrics
		setMetrics({
			startTime: null,
			endTime: null,
			completedCount: 0,
			errorCount: 0,
			processingTimes: [],
			responseLengths: [],
		});
	};

	// Remove a single prompt
	const handleRemovePrompt = (id: string) => {
		setPrompts((prev) => prev.filter((p) => p.id !== id));
	};

	// Reset a prompt's result
	const handleResetPrompt = (id: string) => {
		setPrompts((prev) =>
			prev.map((p) => (p.id === id ? { ...p, status: 'pending', result: undefined, error: undefined } : p))
		);
	};

	const MAX_RETRIES = 15;

	// Start processing from beginning or resume
	const handleStartProcessing = () => {
		if (prompts.length === 0) return;

		// Find first pending or error prompt that hasn't exceeded max retries
		const firstPending = prompts.findIndex(
			(p) => (p.status === 'pending' || p.status === 'error') && p.retryCount < MAX_RETRIES
		);
		if (firstPending === -1) return; // All completed or all exceeded retries

		// Increment request ID here, before setting state
		requestIdRef.current += 1;

		setCurrentIndex(firstPending);
		setIsProcessing(true);
	};

	// Stop processing
	const handleStopProcessing = () => {
		if (currentIndex >= 0 && prompts[currentIndex]?.status === 'processing') {
			cancelChatStream(requestIdRef.current);
		}
		setIsProcessing(false);
	};

	// Build stream args for current prompt
	const streamArgs = useMemo(() => {
		if (currentIndex < 0 || !prompts[currentIndex]) return skipToken;
		return {
			submission: { text: prompts[currentIndex].text, attachments: [] },
			chatId: undefined,
			requestId: requestIdRef.current,
			selectedSystem,
			selectedFacility,
			earliest_date: earliest_date?.toISOString(),
			end_date: endDate.toISOString(),
			start_date: startDate.toISOString(),
			surgeons: surgeons?.all,
			serviceLines: serviceLines?.all,
			rooms: rooms?.all,
			encounterTypes: encounterTypes?.all,
			blockNames: blockNames?.all,
		};
	}, [
		currentIndex,
		prompts,
		selectedSystem,
		selectedFacility,
		earliest_date,
		endDate,
		startDate,
		surgeons,
		serviceLines,
		rooms,
		encounterTypes,
		blockNames,
	]);

	const { data: streamData } = useChatStreamQuery(streamArgs);

	// Handle streaming completion
	const prevDoneRef = useRef(false);
	React.useEffect(() => {
		if (!isProcessing || currentIndex < 0) return;

		const currentPrompt = prompts[currentIndex];
		if (!currentPrompt || currentPrompt.status !== 'processing') return;
		const processedPromptId = currentPrompt.id;

		// CRITICAL: Validate that this stream data is for the current request
		// This prevents race conditions where a delayed response from an old prompt
		// could be incorrectly processed as the current prompt's response
		if (streamData?.requestId !== undefined && streamData.requestId !== requestIdRef.current) {
			return;
		}

		// Only trigger on transition to done
		if (streamData?.done && !prevDoneRef.current) {
			prevDoneRef.current = true;

			// Check if response contains attempt_completion
			const hasCompletion = streamData.text && /attempt_completion/i.test(streamData.text);
			const endTime = Date.now();
			const responseLength = streamData.text?.length || 0;

			// Get the prompt to calculate processing time
			const processedPrompt = prompts[currentIndex];
			const processingTime = processedPrompt?.startTime ? endTime - processedPrompt.startTime : 0;
			const currentRetryCount = processedPrompt?.retryCount || 0;

			if (hasCompletion) {
				const responseText = streamData.text || '';

				setPrompts((prev) =>
					prev.map((p) =>
						p.id === processedPromptId
							? {
									...p,
									status: 'completed',
									result: responseText || p.result,
									error: undefined,
									endTime,
									responseLength,
							  }
							: p
					)
				);

				// Update metrics with this prompt's data
				setMetrics((prev) => ({
					...prev,
					completedCount: prev.completedCount + 1,
					processingTimes: [...prev.processingTimes, processingTime],
					responseLengths: [...prev.responseLengths, responseLength],
					endTime: currentIndex + 1 >= prompts.length ? endTime : prev.endTime,
				}));

				// Auto-advance to next prompt after completion
				setTimeout(() => {
					moveToNextPrompt();
					prevDoneRef.current = false;
				}, 500);
			} else {
				// Failure - check if we can retry
				if (currentRetryCount < MAX_RETRIES) {
					// Retry the prompt
					setPrompts((prev) =>
						prev.map((p) =>
							p.id === processedPromptId
								? {
										...p,
										status: 'pending',
										result: undefined,
										error: `Attempt ${currentRetryCount + 1} failed - will retry (${currentRetryCount + 1}/${MAX_RETRIES})`,
										retryCount: currentRetryCount + 1,
										startTime: undefined,
										endTime: undefined,
								  }
								: p
						)
					);

					// Increment request ID for retry
					requestIdRef.current += 1;

					// Auto-advance to next prompt (which will pick up this one again if no others are pending)
					setTimeout(() => {
						moveToNextPrompt();
						prevDoneRef.current = false;
					}, 500);
				} else {
					// Max retries exceeded - mark as error
					setPrompts((prev) =>
						prev.map((p) =>
							p.id === processedPromptId
								? {
										...p,
										status: 'error',
										result: streamData.text || p.result,
										error: `Prompt did not contain attempt_completion in response after ${MAX_RETRIES} attempts`,
										endTime,
										responseLength,
								  }
								: p
						)
					);

					// Update metrics with this prompt's data (as error)
					setMetrics((prev) => ({
						...prev,
						errorCount: prev.errorCount + 1,
						endTime: currentIndex + 1 >= prompts.length ? endTime : prev.endTime,
					}));

					// Auto-advance to next prompt after a short delay
					setTimeout(() => {
						moveToNextPrompt();
						prevDoneRef.current = false;
					}, 500);
				}
			}

			// Helper function to move to next prompt
			const moveToNextPrompt = () => {
				// Find the next pending or error prompt that hasn't exceeded max retries
				const nextIndex = prompts.findIndex(
					(p, idx) => idx > currentIndex && (p.status === 'pending' || p.status === 'error') && p.retryCount < MAX_RETRIES
				);

				if (nextIndex !== -1) {
					requestIdRef.current += 1;
					setCurrentIndex(nextIndex);
				} else {
					// Wrap around to check for any earlier prompts that need processing
					const wrapIndex = prompts.findIndex(
						(p) => (p.status === 'pending' || p.status === 'error') && p.retryCount < MAX_RETRIES
					);

					if (wrapIndex !== -1 && wrapIndex <= currentIndex) {
						requestIdRef.current += 1;
						setCurrentIndex(wrapIndex);
					} else {
						// All done
						setIsProcessing(false);
						setCurrentIndex(-1);
						// Set final end time when all done
						setMetrics((prev) => ({ ...prev, endTime: Date.now() }));
					}
				}
			};
		} else if (!streamData?.done) {
			prevDoneRef.current = false;
		}
	}, [streamData?.done, isProcessing, currentIndex, prompts]);

	// Track benchmark start time when processing begins
	React.useEffect(() => {
		if (isProcessing && metrics.startTime === null) {
			setMetrics((prev) => ({ ...prev, startTime: Date.now() }));
		}
	}, [isProcessing, metrics.startTime]);

	// Update prompt status to processing when starting
	React.useEffect(() => {
		if (isProcessing && currentIndex >= 0) {
			setPrompts((prev) =>
				prev.map((p, idx) =>
					idx === currentIndex
						? {
								...p,
								status: 'processing',
								result: p.status === 'processing' ? p.result : undefined,
								error: p.status === 'processing' ? p.error : undefined,
								startTime: Date.now(),
						  }
						: p
				)
			);
		}
	}, [isProcessing, currentIndex]);

	// Get completion stats
	const completedCount = prompts.filter((p) => p.status === 'completed').length;
	const errorCount = prompts.filter((p) => p.status === 'error').length;

	// Calculate derived metrics
	const totalElapsedTime = metrics.endTime && metrics.startTime ? metrics.endTime - metrics.startTime : null;
	const averageProcessingTime =
		metrics.processingTimes.length > 0
			? metrics.processingTimes.reduce((a, b) => a + b, 0) / metrics.processingTimes.length
			: null;
	const successRate = prompts.length > 0 ? (completedCount / prompts.length) * 100 : 0;
	const errorRate = prompts.length > 0 ? (errorCount / prompts.length) * 100 : 0;

	// Get processing time for a prompt
	const getProcessingTime = (prompt: BenchmarkItem) => {
		if (prompt.startTime && prompt.endTime) {
			return `${((prompt.endTime - prompt.startTime) / 1000).toFixed(2)}s`;
		}
		return '-';
	};

	// Status badge component
	const StatusBadge = ({ status, retryCount }: { status: BenchmarkItem['status']; retryCount: number }) => {
		const styles = {
			pending: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
			processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 animate-pulse',
			completed: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
			error: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
		};
		const labels = {
			pending: 'Pending',
			processing: 'Processing',
			completed: 'PASS',
			error: 'FAIL',
		};

		// Show retry indicator for pending items that have been retried
		const showRetryBadge = status === 'pending' && retryCount > 0;

		return (
			<div className='flex items-center gap-2'>
				<span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>{labels[status]}</span>
				{showRetryBadge && (
					<span className='px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'>
						{retryCount}/{MAX_RETRIES}
					</span>
				)}
			</div>
		);
	};

	return (
		<div className='bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-950'>
			<div className='max-w-full mx-auto p-6 overflow-auto' style={{ maxHeight: 'calc(100vh - 120px)' }}>
				{/* Header */}
				<div className='mb-6'>
					<h1 className='text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent'>
						Benchmarking Tool
					</h1>
					<p className='text-gray-600 dark:text-gray-400 mt-1 text-sm'>
						Test and benchmark multiple AI prompts to evaluate performance
					</p>
				</div>

				{/* Input Area */}
				<div className='bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 mb-6'>
					<h2 className='text-base font-semibold mb-3 flex items-center gap-2'>
						<Settings2 size={18} />
						Benchmark Prompts
					</h2>
					<p className='text-sm text-gray-500 dark:text-gray-400 mb-3'>Enter one benchmark prompt per line</p>
					<TextArea
						label=''
						value={rawInput}
						onChange={(e) => setRawInput(e.target.value)}
						placeholder={`Enter prompts to benchmark, one per line:\nWhat is our block utilization?\nShow me the turnover time trends\nList all surgeons`}
						className='min-h-[100px] text-sm'
					/>
					<div className='flex gap-3 mt-3'>
						<Button onClick={handleAddBenchmarks} disabled={!rawInput.trim()} variant='primary'>
							<Sparkles size={14} className='mr-2' />
							Add Benchmarks
						</Button>
						<Button onClick={handleClearAll} disabled={prompts.length === 0} variant='secondary'>
							<Trash2 size={14} className='mr-2' />
							Clear All
						</Button>
						{!isProcessing && prompts.length > 0 && completedCount < prompts.length && (
							<Button onClick={handleStartProcessing} variant='primary'>
								<Play size={14} className='mr-2' />
								{completedCount > 0 ? 'Continue Benchmarking' : 'Start Benchmarking'}
							</Button>
						)}
						{isProcessing && (
							<Button onClick={handleStopProcessing} variant='secondary'>
								<Square size={14} className='mr-2' />
								Stop
							</Button>
						)}
					</div>
				</div>

				{/* Metrics Summary Bar */}
				{prompts.length > 0 && (
					<div className='bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 mb-6'>
						<div className='flex flex-wrap items-center gap-6 text-sm'>
							<div className='flex items-center gap-2'>
								<span className='text-gray-500 dark:text-gray-400'>Progress:</span>
								<div className='w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden'>
									<div
										className='h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300'
										style={{ width: `${(completedCount / prompts.length) * 100}%` }}
									/>
								</div>
								<span className='font-medium'>
									{completedCount}/{prompts.length}
								</span>
							</div>
							<div className='flex items-center gap-4'>
								<div>
									<span className='text-gray-500 dark:text-gray-400'>Total Time:</span>{' '}
									<span className='font-medium'>
										{totalElapsedTime !== null ? `${(totalElapsedTime / 1000).toFixed(1)}s` : '-'}
									</span>
								</div>
								<div>
									<span className='text-gray-500 dark:text-gray-400'>Avg Response:</span>{' '}
									<span className='font-medium'>
										{averageProcessingTime !== null ? `${(averageProcessingTime / 1000).toFixed(2)}s` : '-'}
									</span>
								</div>
								<div>
									<span className='text-gray-500 dark:text-gray-400'>Success:</span>{' '}
									<span className='font-medium text-green-600 dark:text-green-400'>{successRate.toFixed(1)}%</span>
								</div>
								{errorCount > 0 && (
									<div>
										<span className='text-gray-500 dark:text-gray-400'>Errors:</span>{' '}
										<span className='font-medium text-red-600 dark:text-red-400'>
											{errorCount} ({errorRate.toFixed(1)}%)
										</span>
									</div>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Spreadsheet View */}
				{prompts.length > 0 && (
					<div className='bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden'>
						{/* Table Header */}
						<div className='flex justify-between items-center px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider'>
							<div className='w-10 flex-shrink-0'>#</div>
							<div className='flex-grow min-w-[200px] max-w-[300px]'>Prompt</div>
							<div className='w-28 flex-shrink-0'>Status</div>
							<div className='w-24 flex-shrink-0'>Time</div>
							<div className='w-20 flex-shrink-0'>Length</div>
							<div className='w-24 flex-shrink-0 text-center'>Actions</div>
						</div>

						{/* Table Body */}
						<div className='max-h-[500px] overflow-y-auto'>
							{prompts.map((prompt, idx) => (
								<div key={prompt.id}>
									{/* Main Row */}
									<div
										className={`flex justify-between items-center px-4 py-2 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors ${
											idx === currentIndex ? 'bg-blue-50 dark:bg-blue-900/20' : ''
										} ${prompt.status === 'error' ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}
									>
										{/* Index */}
										<div className='w-10 flex-shrink-0 flex items-center'>
											<span
												className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
													prompt.status === 'completed'
														? 'bg-green-500 text-white'
														: prompt.status === 'error'
														? 'bg-red-500 text-white'
														: prompt.status === 'processing'
														? 'bg-blue-500 text-white animate-pulse'
														: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
												}`}
											>
												{idx + 1}
											</span>
										</div>

										{/* Prompt Text */}
										<div className='flex-grow min-w-[200px] max-w-[300px] flex items-center overflow-hidden'>
											<span
												className={`text-xs truncate ${
													prompt.status === 'processing' ? 'font-medium text-blue-600 dark:text-blue-400' : ''
												}`}
												title={prompt.text}
											>
												{prompt.text}
											</span>
										</div>

										{/* Status */}
										<div className='w-28 flex-shrink-0 flex items-center'>
											<StatusBadge status={prompt.status} retryCount={prompt.retryCount} />
										</div>

										{/* Response Time */}
										<div className='w-24 flex-shrink-0 flex items-center text-sm text-gray-600 dark:text-gray-400'>
											{getProcessingTime(prompt)}
										</div>

										{/* Response Length */}
										<div className='w-20 flex-shrink-0 flex items-center text-sm text-gray-600 dark:text-gray-400'>
											{prompt.responseLength ? `${prompt.responseLength.toLocaleString()}` : '-'}
										</div>

										{/* Actions */}
										<div className='w-24 flex-shrink-0 flex items-center justify-center gap-1'>
											{(prompt.status === 'completed' || prompt.status === 'error') && (
												<button
													onClick={() => toggleRowExpansion(prompt.id)}
													className='p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded'
													title={expandedRows.has(prompt.id) ? 'Collapse' : 'Expand'}
												>
													{expandedRows.has(prompt.id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
												</button>
											)}
											{prompt.status === 'completed' && (
												<button
													onClick={() => handleResetPrompt(prompt.id)}
													className='p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded'
													title='Reset'
												>
													<RotateCcw size={14} />
												</button>
											)}
											<button
												onClick={() => handleRemovePrompt(prompt.id)}
												disabled={isProcessing}
												className='p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded'
												title='Remove'
											>
												<Trash2 size={14} />
											</button>
										</div>
									</div>

									{/* Expanded Result - Full Width Accordion */}
									{(expandedRows.has(prompt.id) || prompt.status === 'processing') && (
										<div className='w-full px-4 pb-3 bg-gray-50 dark:bg-gray-900/30 space-y-2'>
											{prompt.status === 'processing' ? (
												<div className='flex items-center gap-3 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3'>
													<div className='animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent' />
													<span className='text-sm'>Processing benchmark...</span>
												</div>
											) : prompt.status === 'error' ? (
												<div className='text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-sm'>
													{prompt.error || 'An error occurred'}
												</div>
											) : (
												<>
													{prompt.result && (
														<div className='bg-white dark:bg-gray-800 rounded-lg p-3 max-h-[200px] overflow-y-auto border border-gray-200 dark:border-gray-700'>
															<div className='text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2'>Response:</div>
															<div className='prose prose-sm dark:prose-invert max-w-none'>
																<ChatMarkdown text={prompt.result} fontSize='sm' instant />
															</div>
														</div>
													)}
												</>
											)}
										</div>
									)}
								</div>
							))}
						</div>

						{prompts.length === 0 && (
							<div className='text-center py-16 text-gray-400 dark:text-gray-500'>
								<Sparkles size={48} className='mx-auto mb-4 opacity-50' />
								<p>No benchmarks added yet</p>
								<p className='text-sm mt-1'>Enter prompts above to get started</p>
							</div>
						)}
					</div>
				)}

				{/* Empty State */}
				{prompts.length === 0 && (
					<div className='bg-white dark:bg-gray-800 rounded-xl shadow-lg p-12 text-center'>
						<Sparkles size={64} className='mx-auto mb-4 text-gray-300 dark:text-gray-600' />
						<h2 className='text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2'>No Benchmarks Yet</h2>
						<p className='text-gray-500 dark:text-gray-400 mb-4'>
							Enter your prompts above to start benchmarking AI responses
						</p>
						<p className='text-sm text-gray-400 dark:text-gray-500'>
							Enter one prompt per line, then click &quot;Add Benchmarks&quot;
						</p>
					</div>
				)}
			</div>
		</div>
	);
}
